// ------------------------------------------------------------------
// Presell VARIANT: GROK
// Faithful React port of presell-mockups/presell-GROK.html.
// Dense editorial business-tool layout: split dark hero, red top-stripe
// control cards, left-rail numbered steps + dark proof callout, 2-col
// copy|table pricing, break-even math table + red upgrade box, FB-vs-
// BigLoads compare, connected 4-step search-journey rail, pain chips,
// 2-col resources, dashboard panel, 2-col FAQ, black Haul Star close,
// image final CTA. Header/footer come from the marketplace shell.
// ------------------------------------------------------------------

import React from 'react';
import { func } from 'prop-types';

import heroImg from '../../assets/presell/hero-large.webp';
import dumpImg from '../../assets/presell/dump-trailer.jpg';
import carHaulerImg from '../../assets/presell/car-hauler.jpg';
import logoInverted from '../../assets/presell/logo-clean-inverted.png';

import {
  CHANNELS,
  TABLE_ROWS,
  STATS,
  FAQ,
  COPY,
  CONTROL_LONG,
  STEPS_LONG,
  MATH_ROWS,
  VALUE_HAUL_ITEMS,
  FB_COMPARE,
  JOURNEY,
  PAIN_CHIPS,
  RESOURCE_LIST,
  HS_FEATURES,
  GROK,
} from './presellContent';
import css from './GrokLayout.module.css';

const channelIconBg = ['#E7F0FF', '#FFF3E0', '#E8F5E9', '#F3E5F5'];

const GrokLayout = ({ listFreeLink, haulStarLink, newsletterLink }) => (
  <div className={css.root}>
    {/* ===== HERO ===== */}
    <section className={css.hero}>
      <div className={css.heroInner}>
        <div className={css.heroCopy}>
          <span className={css.heroEyebrow}>{GROK.heroEyebrow}</span>
          <h1 className={css.heroH1}>
            Put Your Trailers
            <br />
            in Front of <span>More Renters.</span>
          </h1>
          <p className={css.heroSub}>{COPY.lede}</p>
          <div className={css.heroCtas}>
            {listFreeLink(
              `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-list-free`,
              'hero',
              COPY.ctaListFree
            )}
            <a href="#how" className={`${css.btn} ${css.btnOutlineWhite} ${css.btnLg}`}>
              {COPY.ctaHowItWorks}
            </a>
          </div>
          <p className={css.heroMicrotrust}>
            <strong>No commissions.</strong> &nbsp;No booking fees. &nbsp;No new rental software to
            learn.
          </p>
        </div>
        <div className={css.heroImageCol}>
          <div className={css.heroImageOverlay} />
          <img src={heroImg} alt="Trailer fleet ready for rental" loading="eager" />
          <div className={css.heroBadge}>{GROK.heroBadge}</div>
        </div>
      </div>
    </section>

    {/* ===== CHANNEL ===== */}
    <section className={`${css.section} ${css.channel}`}>
      <div className={css.wrap}>
        <h2 className={css.channelHeadline}>
          {GROK.channelHeadA}
          <br />
          <span>{GROK.channelHeadB}</span>
        </h2>
        <div className={css.channelGrid}>
          <div className={css.existingChannels}>
            {CHANNELS.map((c, i) => (
              <div key={c.h} className={css.channelCard}>
                <div className={css.channelIcon} style={{ background: channelIconBg[i] }}>
                  {c.ic}
                </div>
                <div className={css.channelName}>{c.h}</div>
                <div className={css.channelDesc}>{c.p}</div>
              </div>
            ))}
          </div>
          <div className={css.channelPlus}>+</div>
          <div>
            <div className={css.bigloadsChannel}>
              <img src={logoInverted} alt="BigLoads" />
              <div className={css.bigloadsChannelTag}>{GROK.channelBlTag}</div>
              <div className={css.bigloadsChannelCopy}>{GROK.channelBlCopy}</div>
            </div>
            <div className={css.channelRightCopy}>
              <p>{GROK.channelP1}</p>
              <p>{GROK.channelP2}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ===== CONTROL ===== */}
    <section className={`${css.section} ${css.control}`}>
      <div className={css.wrap}>
        <div className={css.controlTitleBlock}>
          <div className={css.sectionLabel}>{GROK.controlLabel}</div>
          <h2 className={`${css.sectionTitle} ${css.titleXl}`}>
            Your Trailers. Your Rates.
            <br />
            Your Customers. Your Rules.
          </h2>
        </div>
        <div className={css.controlCards}>
          {CONTROL_LONG.map(c => (
            <div key={c.h} className={css.controlCard}>
              <div className={css.controlCardIcon}>{c.ic}</div>
              <div className={css.controlCardTitle}>{c.h}</div>
              <div className={css.controlCardDesc}>{c.p}</div>
            </div>
          ))}
        </div>
        <div className={css.controlFooter}>
          {GROK.controlFootA}
          <span>{GROK.controlFootB}</span>
        </div>
      </div>
    </section>

    {/* ===== HOW IT WORKS ===== */}
    <section className={`${css.section} ${css.hiw}`} id="how">
      <div className={css.wrap}>
        <div className={css.hiwGrid}>
          <div>
            <div className={css.sectionLabel}>{GROK.hiwLabel}</div>
            <h2 className={`${css.sectionTitle} ${css.titleLg}`} style={{ marginBottom: 10 }}>
              {GROK.hiwTitleA}
              <br />
              {GROK.hiwTitleB}
            </h2>
            <div className={css.redRule} />
            <div className={css.hiwSteps}>
              {STEPS_LONG.map(s => (
                <div key={s.n} className={css.hiwStep}>
                  <div className={css.hiwNum}>{s.n}</div>
                  <div>
                    <div className={css.hiwStepTitle}>{s.h}</div>
                    <div className={css.hiwStepDesc}>{s.p}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 28 }}>
              {listFreeLink(`${css.btn} ${css.btnPrimary} cta-list-free`, 'how', COPY.ctaListFree)}
            </div>
          </div>
          <div className={css.hiwCallout}>
            <div className={css.hiwCalloutImg}>
              <img src={carHaulerImg} alt="Trailer rental fleet" loading="lazy" />
            </div>
            <div className={css.hiwCalloutProof}>
              {GROK.hiwProofA}
              <span>{GROK.hiwProofRed}</span>
              {GROK.hiwProofB}
            </div>
            <div className={css.hiwCalloutSub}>{GROK.hiwProofSub}</div>
            {haulStarLink(
              `${css.btn} ${css.btnPrimary} cta-haul-star`,
              'how',
              COPY.ctaBecomeHaulStar
            )}
          </div>
        </div>
      </div>
    </section>

    {/* ===== PRICING ===== */}
    <section className={`${css.section} ${css.pricing}`} id="pricing">
      <div className={css.wrap}>
        <div className={css.pricingGrid}>
          <div>
            <div className={css.sectionLabel}>{GROK.pricingLabel}</div>
            <h2 className={`${css.sectionTitle} ${css.titleLg}`} style={{ marginBottom: 10 }}>
              {GROK.pricingTitle}
            </h2>
            <div className={css.redRule} />
            <p className={css.sectionSub} style={{ marginBottom: 24 }}>
              {GROK.pricingSub}
            </p>
            <div className={css.pricingCtaBlock}>
              {haulStarLink(
                `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-haul-star`,
                'pricing',
                COPY.ctaHaulStarStart
              )}
              <div className={css.pricingCtaOr}>or</div>
              {listFreeLink(
                `${css.btn} ${css.btnSecondary} cta-list-free`,
                'pricing',
                COPY.ctaListFree
              )}
            </div>
            <p className={css.pricingFinePrint}>{GROK.pricingFine}</p>
          </div>
          <div>
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className={css.thFree}>Free Listing</th>
                    <th className={css.thHaul}>
                      Haul Star <span className={css.thHaulBadge}>Most Popular</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {TABLE_ROWS.map(r => (
                    <tr key={r.f}>
                      <td>{r.f}</td>
                      <td>
                        {r.free === true ? (
                          <span className={css.check}>✓</span>
                        ) : r.free === false ? (
                          <span className={css.dash}>−</span>
                        ) : (
                          r.free
                        )}
                      </td>
                      <td className={css.tdHaul}>
                        {r.hs === true ? <span className={css.check}>✓</span> : r.hs}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>Price</td>
                    <td>
                      <span className={css.priceFree}>$0</span>
                    </td>
                    <td className={css.tdHaul}>
                      <span className={css.priceHaul}>$30/mo</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={css.tableNote}>
              <strong>{GROK.tableNoteA}</strong>
              {GROK.tableNoteB}
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ===== VALUE ===== */}
    <section className={`${css.section} ${css.value}`}>
      <div className={css.wrap}>
        <div className={css.valueGrid}>
          <div>
            <div className={css.sectionLabel}>{GROK.valueLabel}</div>
            <h2 className={`${css.sectionTitle} ${css.titleLg}`} style={{ marginBottom: 10 }}>
              {GROK.valueTitle}
            </h2>
            <div className={css.redRule} />
            <p className={css.valueLede}>
              {GROK.valueLedeA}
              <strong>{GROK.valueLedeStrong}</strong>
            </p>
            <div className={css.valueMathBox}>
              {MATH_ROWS.map(m => (
                <div key={m.label} className={css.valueMathRow}>
                  <span className={css.valueMathLabel}>{m.label}</span>
                  <span className={`${css.valueMathVal}${m.green ? ' ' + css.green : ''}`}>
                    {m.val}
                  </span>
                </div>
              ))}
            </div>
            <p className={css.valueSmall}>{GROK.valueSmall}</p>
            <div className={css.valueProof}>
              <p className={css.valueProofBig}>
                {COPY.proofBigA}
                <span>{COPY.proofBigRed}</span>
                {COPY.proofBigB}
              </p>
              <p className={css.valueProofStat}>{COPY.proofSmall}</p>
              <p className={css.valueProofItalic}>
                Views don&rsquo;t guarantee bookings. But zero views guarantee zero bookings.
              </p>
            </div>
          </div>
          <div className={css.valueHaulBox}>
            <div className={css.valueHaulTag}>{GROK.valueHaulTag}</div>
            <div className={css.valueHaulPrice}>$30</div>
            <div className={css.valueHaulPeriod}>per month</div>
            <div className={css.valueHaulNote}>{GROK.valueHaulNote}</div>
            <ul className={css.valueHaulItems}>
              {VALUE_HAUL_ITEMS.map(v => (
                <li key={v}>{v}</li>
              ))}
            </ul>
            {haulStarLink(
              `${css.btn} ${css.btnDark} ${css.btnLg} ${css.btnBlock} cta-haul-star`,
              'value',
              COPY.ctaBecomeHaulStar
            )}
            <p className={css.valueHaulFine}>{GROK.valueHaulFine}</p>
          </div>
        </div>
      </div>
    </section>

    {/* ===== FACEBOOK ===== */}
    <section className={`${css.section} ${css.fb}`}>
      <div className={css.wrap}>
        <div className={css.fbGrid}>
          <div>
            <div className={css.sectionLabel}>{GROK.fbLabel}</div>
            <h2 className={`${css.sectionTitle} ${css.titleLg}`} style={{ marginBottom: 10 }}>
              {GROK.fbTitle}
            </h2>
            <div className={css.redRule} />
            <p className={css.fbP}>{GROK.fbP1}</p>
            <p className={css.fbP}>{GROK.fbP2}</p>
            <div className={css.fbCompare}>
              <div className={css.fbCol}>
                <div className={`${css.fbColHead} ${css.fbColHeadFb}`}>{GROK.fbColFb}</div>
                <div className={css.fbColBody}>
                  {FB_COMPARE.fb.map(i => (
                    <div key={i.t} className={css.fbItem}>
                      <span className={css.icon}>{i.ic}</span>
                      {i.t}
                    </div>
                  ))}
                </div>
              </div>
              <div className={css.fbCol}>
                <div className={`${css.fbColHead} ${css.fbColHeadBl}`}>{GROK.fbColBl}</div>
                <div className={css.fbColBody}>
                  {FB_COMPARE.bl.map(i => (
                    <div key={i.t} className={css.fbItem}>
                      <span className={css.icon}>{i.ic}</span>
                      {i.t}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={css.listingCard}>
              <div className={css.listingCardImg}>
                <img src={dumpImg} alt="7x14 Dump Trailer" loading="lazy" />
                <div className={css.listingCardBadge}>{GROK.listingBadge}</div>
              </div>
              <div className={css.listingCardBody}>
                <div className={css.listingCardTitle}>7x14 Dump Trailer</div>
                <div className={css.listingCardBiz}>{GROK.listingBiz}</div>
                <div className={css.listingCardMeta}>
                  <span>📍 Grand Rapids, MI</span>
                  <span>⚖ 14,000 lb GVWR</span>
                  <span>📅 Daily / Weekly Rates</span>
                </div>
                {listFreeLink(
                  `${css.listingCardCta} cta-list-free`,
                  'demo-call-owner',
                  '📞 Call Owner'
                )}
                <div className={css.listingCardNote}>{GROK.listingNote}</div>
              </div>
            </div>
            <p className={css.fbRightCap}>{GROK.fbRightCap}</p>
            <div className={css.fbRightCta}>
              {listFreeLink(
                `${css.btn} ${css.btnPrimary} cta-list-free`,
                'facebook',
                COPY.ctaListFree
              )}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ===== SEARCH JOURNEY ===== */}
    <section className={`${css.section} ${css.journey}`}>
      <div className={css.wrap}>
        <div className={css.journeyTitle}>
          <div className={css.sectionLabel}>{GROK.journeyLabel}</div>
          <h2 className={`${css.sectionTitle} ${css.titleLg}`}>
            {GROK.journeyTitleA}
            <br />
            {GROK.journeyTitleB}
          </h2>
        </div>
        <div className={css.journeySteps}>
          {JOURNEY.map(j => (
            <div key={j.label} className={css.journeyStep}>
              <div className={css.journeyIcon}>{j.ic}</div>
              <div className={css.journeyStepLabel}>{j.label}</div>
              {j.query ? <div className={css.journeyQuery}>{j.query}</div> : null}
              <div className={css.journeyStepText}>{j.t}</div>
            </div>
          ))}
        </div>
        <div className={css.journeyProof}>{GROK.journeyProof}</div>
        <div className={css.journeyCtaRow}>
          {listFreeLink(
            `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-list-free`,
            'journey',
            COPY.ctaListFree
          )}
          {haulStarLink(
            `${css.btn} ${css.btnSecondary} ${css.btnLg} cta-haul-star`,
            'journey',
            GROK.journeyCtaHaulStar
          )}
        </div>
      </div>
    </section>

    {/* ===== PAIN CHIPS ===== */}
    <section className={`${css.section} ${css.painChips}`}>
      <div className={css.wrap}>
        <div className={css.painIntro}>
          <div className={css.sectionLabel}>{GROK.painLabel}</div>
          <h2 className={`${css.sectionTitle} ${css.titleLg}`}>
            {GROK.painTitleA}
            <br />
            {GROK.painTitleB}
          </h2>
          <div className={css.redRule} />
        </div>
        <div className={css.painGrid}>
          {PAIN_CHIPS.map(p => (
            <div key={p.h} className={css.painChip}>
              <div className={css.painChipIcon}>{p.ic}</div>
              <div>
                <div className={css.painChipTitle}>{p.h}</div>
                <div className={css.painChipDesc}>{p.p}</div>
              </div>
            </div>
          ))}
        </div>
        <div className={css.painFooter}>
          <div className={css.painFooterText}>
            {GROK.painFootA}
            <span>{GROK.painFootRed}</span>
          </div>
          <a href="#resources" className={`${css.btn} ${css.btnPrimary}`}>
            {GROK.painFootCta}
          </a>
        </div>
      </div>
    </section>

    {/* ===== RESOURCES ===== */}
    <section className={`${css.section} ${css.resources}`} id="resources">
      <div className={css.wrap}>
        <div className={css.resourcesGrid}>
          <div>
            <div className={css.sectionLabel}>{GROK.resLabel}</div>
            <h2 className={`${css.sectionTitle} ${css.titleLg}`} style={{ marginBottom: 10 }}>
              {GROK.resTitle}
            </h2>
            <div className={css.redRule} />
            <p className={css.resourcesLede}>{GROK.resLede}</p>
            <ul className={css.resourceList}>
              {RESOURCE_LIST.map(r => (
                <li key={r}>{r}</li>
              ))}
            </ul>
            <div className={css.resourceLink}>
              <a href="/toolbox" className={`${css.btn} ${css.btnSecondary}`}>
                {GROK.resLinkCta}
              </a>
            </div>
            <div className={css.newsletterBox}>
              <p className={css.newsletterBoxTitle}>📰 The Weekly Load</p>
              <p className={css.newsletterBoxDesc}>{COPY.newsletterP}</p>
              {newsletterLink(
                `${css.btn} ${css.btnPrimary}`,
                'resources',
                COPY.newsletterCta
              )}
            </div>
          </div>
          <div>
            <p className={css.resourceGuidesLabel}>{GROK.resGuidesLabel}</p>
            <div className={css.resourceGuides}>
              {[carHaulerImg, dumpImg, carHaulerImg].map((img, i) => (
                <a
                  key={GROK.guideLabels[i].short}
                  href="/toolbox"
                  className={css.resourceGuideCard}
                >
                  <div className={css.resourceGuideImg}>
                    <img src={img} alt={GROK.guideLabels[i].long} loading="lazy" />
                    <div className={css.resourceGuideLabel}>
                      <span>{GROK.guideLabels[i].long}</span>
                    </div>
                  </div>
                  <div className={css.resourceGuideFooter}>{GROK.guideLabels[i].short}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ===== DASHBOARD ===== */}
    <section className={`${css.section} ${css.dashboard}`}>
      <div className={css.wrap}>
        <div className={css.dashboardGrid}>
          <div className={css.dashboardPanel}>
            <div className={css.dashboardPanelHead}>
              <img src={logoInverted} alt="BigLoads" />
              <div className={css.dashboardPanelHeadTitle}>{GROK.dashPanelTitle}</div>
            </div>
            <div className={css.dashboardPanelBody}>
              <div className={css.statRow}>
                {STATS.map(s => (
                  <div key={s.lbl} className={css.statTile}>
                    <div className={css.statIcon}>{s.ic}</div>
                    <div className={css.statVal}>{s.num}</div>
                    <div className={css.statLabel}>{s.lbl}</div>
                  </div>
                ))}
              </div>
              {listFreeLink(`${css.dashboardCta} cta-list-free`, 'dashboard', COPY.dashCta)}
            </div>
          </div>
          <div className={css.dashboardRight}>
            <div className={css.sectionLabel}>{GROK.dashLabel}</div>
            <h2 className={`${css.sectionTitle} ${css.titleLg}`} style={{ marginBottom: 16 }}>
              {COPY.dashH}
            </h2>
            <p>{GROK.dashRightP1}</p>
            <p>{GROK.dashRightP2}</p>
          </div>
        </div>
      </div>
    </section>

    {/* ===== FAQ ===== */}
    <section className={`${css.section} ${css.faq}`} id="faq">
      <div className={css.wrap}>
        <div className={css.faqGrid}>
          <div>
            <div className={css.faqHeadlineBlock}>
              <div className={css.sectionLabel}>{GROK.faqLabel}</div>
              <h2 className={`${css.sectionTitle} ${css.titleLg}`}>{GROK.faqTitle}</h2>
            </div>
            <div className={css.faqList}>
              {FAQ.map(f => (
                <details key={f.q} className={css.faqItem}>
                  <summary className={css.faqQuestion}>
                    <span className={css.faqQuestionText}>{f.q}</span>
                    <span className={css.faqToggle}>+</span>
                  </summary>
                  <div className={css.faqAnswer}>{f.a}</div>
                </details>
              ))}
            </div>
          </div>
          <div className={css.faqCtaCol}>
            <div className={css.faqCtaBox}>
              <div className={css.faqCtaBoxTitle}>{GROK.faqCtaTitle}</div>
              <div className={css.faqCtaBoxDesc}>{GROK.faqCtaDesc}</div>
              {listFreeLink(
                `${css.btn} ${css.btnPrimary} ${css.btnBlock} cta-list-free`,
                'faq',
                COPY.ctaListFree
              )}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ===== HAUL STAR CLOSE ===== */}
    <section className={css.hsc} id="haulstar">
      <div className={css.wrap}>
        <div className={css.hscInner}>
          <div>
            <span className={css.hscEyebrow}>{GROK.hscEyebrow}</span>
            <h2 className={css.hscTitle}>{COPY.closeH}</h2>
            <p className={css.hscSub}>{COPY.closeLede}</p>
            <div className={css.hscSlogan}>{GROK.hscSlogan}</div>
            <div className={css.hscCtas}>
              {haulStarLink(
                `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-haul-star`,
                'close',
                COPY.ctaBecomeHaulStar
              )}
              {listFreeLink(
                `${css.btn} ${css.btnOutlineWhite} ${css.btnLg} cta-list-free`,
                'close',
                COPY.ctaNotReady
              )}
            </div>
          </div>
          <div className={css.hscRight}>
            <div className={css.hscPlanName}>{GROK.hscPlanName}</div>
            <div className={css.hscPlanPrice}>$30</div>
            <div className={css.hscPlanPeriod}>per month</div>
            <div className={css.hscPlanNote}>{GROK.hscPlanNote}</div>
            <ul className={css.hscFeatures}>
              {HS_FEATURES.map(h => (
                <li key={h}>
                  <span className={css.featCheck}>✓</span>
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ===== FINAL CTA ===== */}
    <section className={css.finalCta}>
      <div className={css.finalCtaBg}>
        <img src={heroImg} alt="" loading="lazy" />
      </div>
      <div className={css.finalCtaOverlay} />
      <div className={`${css.wrap} ${css.finalCtaInner}`}>
        <div className={css.finalCtaContent}>
          <h2 className={css.finalCtaTitle}>
            {GROK.finalTitleA}
            <span>{GROK.finalTitleRed}</span>
          </h2>
          <p className={css.finalCtaSub}>{GROK.finalSub}</p>
          <div className={css.finalCtaButtons}>
            {listFreeLink(
              `${css.btn} ${css.btnPrimary} ${css.btnLg} cta-list-free`,
              'final',
              COPY.ctaListFree
            )}
            {haulStarLink(
              `${css.btn} ${css.btnOutlineWhite} ${css.btnLg} cta-haul-star`,
              'final',
              COPY.ctaHaulStarStart
            )}
          </div>
          <p className={css.finalTagline}>{GROK.finalTagline}</p>
        </div>
      </div>
    </section>
  </div>
);

GrokLayout.propTypes = {
  listFreeLink: func.isRequired,
  haulStarLink: func.isRequired,
  newsletterLink: func.isRequired,
};

export default GrokLayout;
